import {jsPDF} from 'jspdf';
import {getAssessment} from '@/api/feedbackAssessments';
import schoolLogoUrl from '@/assets/logo-schule.png';
import wildFreiLogoUrl from '@/assets/logo-wild_frei_gruen.png';

/**
 * Exportiert einen kompletten Lernbrief als PDF.
 * Reihenfolge:
 *  1) Vorblatt
 *  2) Freigegebene Kompetenzraster je Fach (neue Seite je Fach)
 *     - Deutsch: max. 2 Seiten
 *     - Alle anderen Fächer: max. 1 Seite
 *  3) Rückblatt
 *
 * Fußzeile je Seite:
 *  - links: Schülername (Vorname Nachname)
 *  - mitte: 'feedback_letters.footnote' (oder über options.footnote überschrieben)
 *  - rechts: 'x von y' (Zählung pro Lernbrief)
 *
 * Erwartete Options:
 * {
 *   student: { first_name, last_name } | string,   // Name
 *   frontSheet: { birth_date?, place?, class_level?, school_year?, notes? } | null,
 *   backSheet: {
 *     additional_activities?, remarks?,
 *     absences?, learning_times?, tardiness?,
 *     unexcused_absences?, unexcused_learning_times?
 *   } | null,
 *   assessmentIds: number[],                       // IDs der zu druckenden Raster
 *   subjectOrder?: string[],                        // definierte Fachreihenfolge (optional)
 *   footnote?: string,                              // Standard: 'feedback_letters.footnote'
 * }
 */

// --------------------- Utilities ---------------------

function safeStr(v, fallback = '') {
    const s = (v ?? '').toString().trim();
    return s ? s : fallback;
}

function clampPct(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.max(0, Math.min(100, Math.round(x)));
}

function formatDeDate(val) {
    const s = String(val ?? '').trim();
    if (!s) return '';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    try {
        return new Intl.DateTimeFormat('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(d);
    } catch {
        return '';
    }
}

function fileSafe(s) {
    return String(s ?? '')
        .normalize('NFKD')
        .replace(/[^\w\s.-]/g, '')
        .trim()
        .replace(/\s+/g, '_');
}

// Maßeinheit cm → pt
const CM = 28.3464567;
const cm = (n) => n * CM;

// Sehr einfache Blocksätz-Funktion (Zeilenumbruch + Versuch der Justierung, je nach jsPDF-Version)
function textJustified(doc, text, x, y, width) {
    const t = String(text ?? '').trim();
    if (!t) return y;

    const lines = doc.splitTextToSize(t, width);
    const fontSize = doc.getFontSize();
    const lineH = fontSize * 1.4;

    const endsSentence = (line) => {
        const s = String(line ?? '').trim();
        if (!s) return false;
        return /[.!?…]\s*$/.test(s);
    };

    const drawJustifiedLine = (line, lx, ly, maxW) => {
        const s = String(line ?? '').trim();
        if (!s) return;

        // Mehrfach-Spaces normalisieren, damit die Wortabstände sauber berechnet werden
        const words = s.split(/\s+/).filter(Boolean);
        if (words.length <= 1) {
            doc.text(s, lx, ly);
            return;
        }

        const spaceCount = words.length - 1;

        // Gesamtbreite aller Wörter (ohne Spaces)
        let wordsW = 0;
        for (const w of words) wordsW += doc.getTextWidth(w);

        const extra = Math.max(0, maxW - wordsW);
        const gap = extra / spaceCount;

        let cx = lx;
        for (let i = 0; i < words.length; i += 1) {
            const w = words[i];
            doc.text(w, cx, ly);
            cx += doc.getTextWidth(w);
            if (i < words.length - 1) {
                cx += gap;
            }
        }
    };

    let cy = y;

    for (let i = 0; i < lines.length; i += 1) {
        const isLast = i === lines.length - 1;
        const line = String(lines[i] ?? '');

        // Regel:
        // - letzte Zeile immer linksbündig
        // - zusätzlich: wenn die Zeile einen Satz abschließt → linksbündig (kein "Auseinanderziehen")
        if (isLast || endsSentence(line)) {
            doc.text(line, x, cy);
        } else {
            drawJustifiedLine(line, x, cy, width);
        }

        cy += lineH;
    }

    return cy;
}

/**
 * Rendert einfachen "Richtext":
 * - Zeilen, die mit "*" beginnen, werden als Liste mit Punkt (•) gerendert.
 * - Inline-Teile in der Form *text* werden ebenfalls als Bullet-Items gerendert (aus dem Fließtext herausgezogen).
 * - Normale Absätze werden entweder linksbündig oder im Blocksatz gerendert.
 *
 * WICHTIG:
 * - Bei selbst umbrochenen Zeilen (splitTextToSize) darf doc.text NICHT nochmal mit {maxWidth} umbrechen,
 *   sonst stimmt die Höhenberechnung (cy) nicht mehr und Text kann überlappen.
 * - Bei Bullets müssen alle Folgezeilen denselben Einzug (textX) haben wie die erste Zeile.
 */
function textWithBullets(doc, text, x, y, width, {align = 'justify'} = {}) {
    const raw = String(text ?? '').replace(/\r\n/g, '\n').trim();
    if (!raw) return y;

    const fontSize = doc.getFontSize();
    const lineH = fontSize * 1.4;

    const blocks = raw
        .split(/\n{2,}/g) // Absatztrenner: Leerzeilen
        .map(b => b.trim())
        .filter(Boolean);

    const renderText = (t, cy) => {
        const s = String(t ?? '').trim();
        if (!s) return cy;

        if (align === 'left') {
            const wrapped = doc.splitTextToSize(s, width);
            for (const ln of wrapped) {
                doc.text(String(ln), x, cy);
                cy += lineH;
            }
            return cy;
        }

        return textJustified(doc, s, x, cy, width);
    };

    const renderBulletItem = (item, cy) => {
        const bullet = '•';
        const bulletIndent = 14;

        const bulletX = x;
        const textX = x + bulletIndent;

        const wrapW = Math.max(0, width - bulletIndent);
        const wrapped = doc.splitTextToSize(String(item ?? ''), wrapW);

        for (let i = 0; i < wrapped.length; i += 1) {
            const ln = String(wrapped[i] ?? '');
            if (i === 0) {
                doc.text(bullet, bulletX, cy);
            }
            // alle Zeilen exakt gleich eingerückt wie die erste Zeile
            doc.text(ln, textX, cy);
            cy += lineH;
        }

        return cy;
    };

    let cy = y;

    for (const block of blocks) {
        const lines = block.split('\n');

        // Teile in Reihenfolge sammeln: {type:'text'|'bullet', value:string}
        const parts = [];

        for (const rawLine of lines) {
            const line = String(rawLine ?? '').trim();
            if (!line) continue;

            // 1) komplette Zeile als Bullet, wenn sie mit "*" beginnt
            if (/^\*\s*/.test(line)) {
                const item = line.replace(/^\*\s*/, '').trim();
                if (item) parts.push({type: 'bullet', value: item});
                continue;
            }

            // 2) Inline-*...* innerhalb einer normalen Zeile zu Bullets machen (Reihenfolge beibehalten)
            const re = /\*([^*]+)\*/g;
            let lastIdx = 0;
            let m;

            while ((m = re.exec(line)) !== null) {
                const before = line.slice(lastIdx, m.index);
                const bulletText = String(m[1] ?? '').trim();

                if (before.trim()) parts.push({type: 'text', value: before.trim()});
                if (bulletText) parts.push({type: 'bullet', value: bulletText});

                lastIdx = m.index + m[0].length;
            }

            const tail = line.slice(lastIdx);
            if (tail.trim()) parts.push({type: 'text', value: tail.trim()});
        }

        // Rendern: Textteile werden zu Absätzen zusammengezogen; Bullets einzeln
        let textBuf = '';
        let lastWasBullet = false;
        const bulletToTextGap = 10; // Abstand NACH einer Liste, bevor Text weitergeht

        const flushText = () => {
            if (!textBuf.trim()) return;
            cy = renderText(textBuf, cy);
            cy += 6;
            textBuf = '';
            lastWasBullet = false;
        };

        for (const p of parts) {
            if (p.type === 'text') {
                if (lastWasBullet && !textBuf) {
                    cy += bulletToTextGap;
                    lastWasBullet = false;
                }
                textBuf = textBuf ? `${textBuf} ${p.value}` : p.value;
                continue;
            }

            // Bullet: vorherigen Text flushen, dann Bullet rendern
            flushText();
            cy = renderBulletItem(p.value, cy);
            lastWasBullet = true;
        }

        flushText();

        cy += 6; // Abstand zwischen Blöcken
    }

    return cy;
}

// Fett + Unterstrichen: jsPDF kennt kein "underline" als Font-Style → Linie manuell zeichnen
function textBoldUnderlined(doc, text, x, y, {underlineOffset = 2, underlineWidth = 0.75} = {}) {
    const t = String(text ?? '');
    doc.setFont('helvetica', 'bold');
    doc.text(t, x, y);

    const w = doc.getTextWidth(t);
    doc.setLineWidth(underlineWidth);
    doc.line(x, y + underlineOffset, x + w, y + underlineOffset);
}

// --------------------- Bars/Parsing ---------------------

function buildBarSvg(fillPercent, w, h) {
    const p = Math.max(0, Math.min(100, Number(fillPercent) || 0));
    const stroke = 1;
    const innerX = stroke / 2;
    const innerY = stroke / 2;
    const innerW = Math.max(0, w - stroke);
    const innerH = Math.max(0, h - stroke);
    const fillInnerW = Math.max(0, Math.min(innerW, (innerW * p) / 100));

    return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
      <rect x="${innerX}" y="${innerY}" width="${innerW}" height="${innerH}" fill="none" stroke="#000" stroke-width="${stroke}" />
      <rect x="${innerX}" y="${innerY}" width="${fillInnerW}" height="${innerH}" fill="#000" stroke="none" />
    </svg>
  `;
}

async function drawBar(doc, x, y, w, h, percent) {
    const svg = buildBarSvg(percent, w, h);

    if (typeof doc.svg === 'function') {
        try {
            // eslint-disable-next-line no-await-in-loop
            await doc.svg(svg, {x, y, width: w, height: h});
            return;
        } catch {
            // Fallback unten
        }
    }

    try {
        const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
        doc.addImage(dataUrl, 'SVG', x, y, w, h);
        return;
    } catch {
        // Fallback unten
    }

    // Fallback (ohne SVG)
    const p = clampPct(percent);

    doc.setDrawColor(0);
    doc.setLineWidth(1);
    doc.rect(x, y, w, h);

    const inset = 1;

    const fillW = (w * p) / 100;
    if (fillW > 0.1) {
        const fx = x + inset;
        const fy = y + inset;
        const fw = Math.max(0, Math.min(w - 2 * inset, fillW - inset));
        const fh = Math.max(0, h - 2 * inset);

        if (fw > 0.1 && fh > 0.1) {
            doc.setFillColor(0);
            doc.rect(fx, fy, fw, fh, 'F');
        }
    }
}

function parseAssessmentJson(input) {
    if (!input) return {template_snapshot: null, values: {}};
    if (typeof input === 'object') {
        return {
            template_snapshot: input.template_snapshot ?? null,
            values:
                input.values && typeof input.values === 'object'
                    ? input.values
                    : {},
        };
    }
    if (typeof input === 'string') {
        try {
            const o = JSON.parse(input);
            return parseAssessmentJson(o);
        } catch {
            return {template_snapshot: null, values: {}};
        }
    }
    return {template_snapshot: null, values: {}};
}

function getValue(values, key) {
    const v = values?.[key];
    if (!v || typeof v !== 'object') return {absent: false, level: 0};
    return {
        absent: !!v.absent,
        level: clampPct(v.level),
    };
}

// --------------------- Assessment-Rendering ---------------------

function wrapTextLines(doc, text, maxWidth) {
    const t = safeStr(text, '');
    if (!t) return [''];
    const lines = doc.splitTextToSize(t, maxWidth);
    return Array.isArray(lines) && lines.length ? lines : [''];
}

function computeRowHeight(linesCount, {lineHeight, paddingTop, paddingBottom, minHeight}) {
    const h = paddingTop + linesCount * lineHeight + paddingBottom;
    return Math.max(minHeight, h);
}

function drawAssessmentMetaBox(doc, x, y, w, {subject, topic}) {
    const pad = 8;
    const innerW = w - pad * 2;

    doc.setTextColor(0);

    // --- Fach (oben links) ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    const subjectText = safeStr(subject, '');
    const subjectLines = doc.splitTextToSize(subjectText, innerW);

    // --- Inhalte und Themen: + Topic direkt dahinter, max. 3 Zeilen Topic ---
    const label = 'Inhalte und Themen:';
    const topicText = safeStr(topic, '');

    const subjectLineH = 24;
    const bodyLineH = 16;

    // Topic-Wrapping mit unterschiedlicher Breite in Zeile 1 (wegen Label davor)
    const maxTopicLines = 3;
    const topicLines = [];

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    const labelW = doc.getTextWidth(label);
    const gap = 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);

    const words = topicText ? topicText.split(/\s+/).filter(Boolean) : [];
    const availFirst = Math.max(0, innerW - labelW - gap);
    const availNext = innerW;

    const buildLines = (maxLines) => {
        let i = 0;

        const tryPushLine = (availableWidth) => {
            let line = '';
            while (i < words.length) {
                const candidate = line ? `${line} ${words[i]}` : words[i];
                const candidateW = doc.getTextWidth(candidate);
                if (candidateW <= availableWidth || !line) {
                    line = candidate;
                    i += 1;
                } else {
                    break;
                }
            }
            return line;
        };

        // 1. Zeile: weniger Platz, weil Label davor
        if (maxLines > 0) {
            const l1 = tryPushLine(availFirst);
            topicLines.push(l1);
        }

        // Folgezeilen: volle Breite
        while (topicLines.length < maxLines && i < words.length) {
            const ln = tryPushLine(availNext);
            topicLines.push(ln);
        }

        // Falls zu lang: letzte Zeile mit Ellipsis
        if (i < words.length && topicLines.length) {
            let last = topicLines[topicLines.length - 1] || '';
            const ell = '…';
            const availableWidth = (topicLines.length === 1) ? availFirst : availNext;

            if (doc.getTextWidth(last + ell) <= availableWidth) {
                topicLines[topicLines.length - 1] = last + ell;
            } else {
                // hart kürzen, bis es passt
                while (last.length > 0 && doc.getTextWidth(last + ell) > availableWidth) {
                    last = last.slice(0, -1);
                }
                topicLines[topicLines.length - 1] = (last || '').trimEnd() + ell;
            }
        }
    };

    buildLines(maxTopicLines);

    // Box-Höhe berechnen
    const boxH =
        pad +
        (subjectLines.length * subjectLineH) +
        10 +
        (Math.max(1, topicLines.length) * bodyLineH) +
        pad;

    // Rahmen
    doc.setLineWidth(1);
    doc.rect(x, y, w, boxH);

    // Inhalt im Rahmen
    let cy = y + pad + 18;

    // Fach
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    for (const line of subjectLines) {
        doc.text(String(line), x + pad, cy, {maxWidth: innerW});
        cy += subjectLineH;
    }

    cy += 6;

    // Inhalte und Themen: + Topic in derselben Zeile
    const lineX = x + pad;
    const lineY = cy;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(label, lineX, lineY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);

    const firstTopic = topicLines[0] ?? '';
    doc.text(firstTopic, lineX + labelW + gap, lineY, {maxWidth: availFirst});

    cy += bodyLineH;

    // weitere Topic-Zeilen (ohne Label, linkbündig am Rahmen)
    for (let idx = 1; idx < topicLines.length; idx += 1) {
        doc.text(String(topicLines[idx] ?? ''), lineX, cy, {maxWidth: innerW});
        cy += bodyLineH;
    }

    return {height: boxH};
}

function drawCompetenciesHeaderRow(doc, x, y, w, colLabelW, colNotDoneW, title, rightHint) {
    const h = 34;

    // 3) Tabellenheader: leichte BG + zentriert
    doc.setFillColor(242);
    doc.rect(x, y, w, h, 'F');

    doc.setDrawColor(0);
    doc.setLineWidth(1);
    doc.rect(x, y, w, h);

    // vertikale Trenner
    doc.line(x + colLabelW, y, x + colLabelW, y + h);
    doc.line(x + colLabelW + colNotDoneW, y, x + colLabelW + colNotDoneW, y + h);

    // Links: Kompetenzen (zentriert)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(title, x + (colLabelW / 2), y + 20, {align: 'center'});

    // Mitte: "nicht bearbeitet" (zweizeilig, zentriert)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    const midX = x + colLabelW + (colNotDoneW / 2);
    doc.text('nicht', midX, y + 14, {align: 'center'});
    doc.text('bearbeitet', midX, y + 26, {align: 'center'});

    // Rechts: "Einschätzung" (zentriert)
    const rightCellX = x + colLabelW + colNotDoneW;
    const rightCellW = w - colLabelW - colNotDoneW;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Einschätzung', rightCellX + (rightCellW / 2), y + 20, {align: 'center'});

    // rightHint bleibt optional (aktuell ungenutzt)
    if (rightHint) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(rightHint, rightCellX + 10, y + 18, {align: 'left'});
    }
}

async function drawCompetenciesTable(doc, x, y, w, data, onNeedNewPage) {
    const colNotDoneW = 64; // etwas breiter für "nicht bearbeitet"
    const colArrowW = 150;
    const colLabelW = w - colNotDoneW - colArrowW;

    const labelFontSize = 10;
    const labelLineHeight = 12;
    const labelPaddingTop = 4;
    const labelPaddingBottom = 4;

    const minItemRowH = 20;
    const groupRowH = 26;

    const pageBottomY = 780;

    drawCompetenciesHeaderRow(doc, x, y, w, colLabelW, colNotDoneW, 'Kompetenzen', '');
    let cy = y + 34;

    const groups = Array.isArray(data.templateSnapshot?.grouped?.groups) ? data.templateSnapshot.grouped.groups : [];
    const values = data.values || {};

    for (const g of groups) {
        const items = Array.isArray(g.items) ? g.items : [];

        // Gruppen ohne Items können laut Vorgabe nicht vorkommen,
        // daher: echte Höhe der ersten Kompetenzzeile berechnen (nicht nur minItemRowH).
        const firstItem = items[0];
        const labelMaxWidth = colLabelW - 20;
        const firstLines = wrapTextLines(doc, firstItem?.label, labelMaxWidth);
        const firstRowH = computeRowHeight(firstLines.length, {
            lineHeight: labelLineHeight,
            paddingTop: labelPaddingTop,
            paddingBottom: labelPaddingBottom,
            minHeight: minItemRowH,
        });

        // Wenn Gruppe + erste Kompetenz nicht mehr komplett auf die Seite passen:
        // -> Umbruch VOR der Gruppe.
        if (cy + groupRowH + firstRowH > pageBottomY) {
            // eslint-disable-next-line no-await-in-loop
            cy = await onNeedNewPage();
            drawCompetenciesHeaderRow(doc, x, cy, w, colLabelW, colNotDoneW, 'Kompetenzen', '');
            cy += 34;
        } else if (cy + groupRowH > pageBottomY) {
            // eslint-disable-next-line no-await-in-loop
            cy = await onNeedNewPage();
            drawCompetenciesHeaderRow(doc, x, cy, w, colLabelW, colNotDoneW, 'Kompetenzen', '');
            cy += 34;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.rect(x, cy, w, groupRowH);
        doc.text(safeStr(g.title, ''), x + 5, cy + 17);
        cy += groupRowH;

        for (const it of items) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(labelFontSize);

            const labelMaxWidth = colLabelW - 20;
            const lines = wrapTextLines(doc, it.label, labelMaxWidth);

            const rowH = computeRowHeight(lines.length, {
                lineHeight: labelLineHeight,
                paddingTop: labelPaddingTop,
                paddingBottom: labelPaddingBottom,
                minHeight: minItemRowH,
            });

            if (cy + rowH > pageBottomY) {
                // eslint-disable-next-line no-await-in-loop
                cy = await onNeedNewPage();
                drawCompetenciesHeaderRow(doc, x, cy, w, colLabelW, colNotDoneW, 'Kompetenzen', '');
                cy += 34;

                // WICHTIG: Header setzt Font/Size (bold/12) → für Kompetenzzeile wieder zurücksetzen
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(labelFontSize);
            }

            const k = `comp:${String(g.id)}:${String(it.id)}`;
            const v = getValue(values, k);

            doc.rect(x, cy, w, rowH);
            doc.line(x + colLabelW, cy, x + colLabelW, cy + rowH);
            doc.line(x + colLabelW + colNotDoneW, cy, x + colLabelW + colNotDoneW, cy + rowH);

            const textX = x + 5;
            let textY = cy + labelPaddingTop + labelFontSize;
            for (const line of lines) {
                doc.text(String(line), textX, textY, {maxWidth: labelMaxWidth});
                textY += labelLineHeight;
            }

            const cbSize = 10;
            const cbX = x + colLabelW + 27; // zentrierter in breiterer Spalte
            const cbY = cy + (rowH - cbSize) / 2;
            doc.rect(cbX, cbY, cbSize, cbSize);

            if (v.absent) {
                doc.setLineWidth(1);
                doc.line(cbX + 2, cbY + 2, cbX + cbSize - 2, cbY + cbSize - 2);
                doc.line(cbX + cbSize - 2, cbY + 2, cbX + 2, cbY + cbSize - 2);
            }

            const arrowH = 10;
            const barPadL = 12;
            const barPadR = 12;
            const cellLeft = x + colLabelW + colNotDoneW;
            const arrowX = cellLeft + barPadL;
            const arrowY = cy + (rowH - arrowH) / 2;
            const arrowW = Math.max(0, colArrowW - barPadL - barPadR);

            // eslint-disable-next-line no-await-in-loop
            await drawBar(doc, arrowX, arrowY, arrowW, arrowH, v.absent ? 0 : v.level);

            cy += rowH;
        }
    }

    return cy;
}

async function drawAssessmentPagesLimited(doc, assessment, maxPages) {
    const pageW = doc.internal.pageSize.getWidth();
    const leftX = 50;
    const leftW = pageW - 80;

    const aj = parseAssessmentJson(assessment.assessment_json);
    const templateSnapshot = aj.template_snapshot || null;

    const data = {
        date: assessment.assessed_on || assessment.date || '',
        student: assessment.student || '',
        teacher: assessment.teacher || '',
        subject: assessment.subject || templateSnapshot?.subject || '',
        topic: assessment.topic || templateSnapshot?.topic || templateSnapshot?.title || '',
        templateSnapshot,
        values: aj.values || {},
    };

    // 2) Meta-Block oben im Rahmen: Fach + Inhalte/Themen (topic)
    const metaY = 30;
    const meta = drawAssessmentMetaBox(doc, leftX, metaY, leftW, {
        subject: data.subject,
        topic: data.topic,
    });

    const topY = metaY + meta.height + 18;

    let usedPages = 1;
    const onNeedNewPage = async () => {
        if (usedPages >= maxPages) {
            return 1e6;
        }
        doc.addPage('a4', 'portrait');
        usedPages += 1;

        return metaY;
    };

    await drawCompetenciesTable(doc, leftX, topY, leftW, data, onNeedNewPage);

    return usedPages;
}

// --------------------- Front-/Backsheet ---------------------

async function drawFrontSheet(doc, studentName, front) {
    const pageW = doc.internal.pageSize.getWidth();
    const margin = Math.round(cm(2)); // 2 cm

    doc.setTextColor(0);

    // Logos laden (80px Höhe, Breite aus Seitenverhältnis)
    const loadImage = (url) =>
        new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
        });

    let logoImg = null;
    let logoWildImg = null;

    try {
        logoImg = await loadImage(schoolLogoUrl);
        logoWildImg = await loadImage(wildFreiLogoUrl);
    } catch {
        logoImg = null;
    }

    // Kopf
    const titleY = 60;

    // Links/rechts Logo zeichnen (wenn vorhanden)
    if (logoImg) {
        const targetH = 80;
        const ratio = (logoImg.naturalWidth || logoImg.width || targetH) / (logoImg.naturalHeight || logoImg.height || targetH);
        const targetW = Math.max(40, Math.round(targetH * ratio));

        const leftX = margin;
        const rightX = pageW - margin - targetW;
        const topY = Math.max(20, Math.round(titleY - targetH / 2)) + 15;

        try {
            doc.addImage(logoWildImg, 'PNG', leftX, topY, targetW, targetH);
            doc.addImage(logoImg, 'PNG', rightX, topY, targetW, targetH);
        } catch {
            // Ignorieren, wenn addImage fehlschlägt
        }
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('Freie Naturschule Barnim', pageW / 2, titleY - 10, {align: 'center'});

    doc.setFontSize(20);
    doc.text('Lernbericht', pageW / 2, 90, {align: 'center'});

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('für', pageW / 2, 120, {align: 'center'});

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(18);
    doc.text(safeStr(studentName, '—'), pageW / 2, 150, {align: 'center'});

    // Stammdaten
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);

    const yBase = 200;
    const lineGap = 28;

    doc.setFont('helvetica', 'bold');
    doc.text('geboren am:', margin, yBase);
    doc.setFont('helvetica', 'normal');
    doc.text(safeStr(formatDeDate(front?.birthday), '—'), margin + 80, yBase);

    doc.setFont('helvetica', 'bold');
    doc.text('in:', pageW / 2, yBase);
    doc.setFont('helvetica', 'normal');
    doc.text(safeStr(front?.place, '—'), pageW / 2 + 70, yBase);

    doc.setFont('helvetica', 'bold');
    doc.text('Jahrgang:', margin, yBase + lineGap);
    doc.setFont('helvetica', 'normal');
    doc.text(safeStr(front?.grade, '—'), margin + 80, yBase + lineGap);

    doc.setFont('helvetica', 'bold');
    doc.text('Schuljahr:', pageW / 2, yBase + lineGap);
    doc.setFont('helvetica', 'normal');
    doc.text(safeStr(front?.school_year, '—'), pageW / 2 + 70, yBase + lineGap);

    // Fließtext/Hinweise (Blocksatz, 12 pt, Zeilenabstand global)
    const textY = yBase + lineGap * 2 + 40;
    const body = safeStr(front?.notes, '');
    if (body) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        textJustified(doc, body, margin, textY, pageW - 2 * margin);
    }
}

function drawBackSheet(doc, back) {
    const pageW = doc.internal.pageSize.getWidth();
    const margin = Math.round(cm(2)); // 2 cm
    let y = 60;

    doc.setTextColor(0);
    doc.setFontSize(12);
    textBoldUnderlined(doc, 'Zusätzliche Aktivitäten und besonderes Engagement', margin, y);
    y += 24;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    if (back?.additional_activities) {
        // Links bündig (kein Blocksatz) + Sternchen-Zeilen als Bullet-Liste
        y = textWithBullets(
            doc,
            String(back.additional_activities),
            margin,
            y,
            pageW - 2 * margin,
            {align: 'left'}
        ) + 32;
    } else {
        y += 32;
    }

    doc.setFont('helvetica', 'bold');
    textBoldUnderlined(doc, 'Bemerkungen', margin, y);
    y += 24;

    doc.setFont('helvetica', 'normal');
    if (back?.remarks) {
        // Weiterhin Blocksatz, aber Sternchen-Zeilen als Bullet-Liste
        y = textWithBullets(
            doc,
            String(back.remarks),
            margin,
            y,
            pageW - 2 * margin,
            {align: 'justify'}
        ) + 32;
    } else {
        y += 32;
    }

    doc.setFont('helvetica', 'bold');
    textBoldUnderlined(doc, 'Versäumnisse', margin, y);
    y += 24;

    doc.setFont('helvetica', 'normal');
    const rowGap = 20;
    const labelW = 90;

    const drawRow = (label, left, right) => {
        doc.text(`${label}:`, margin, y);
        doc.text(safeStr(left, '—'), margin + labelW, y);
        doc.text('davon unentschuldigt:', margin + 150, y);
        doc.text(safeStr(right, '—'), margin + 280, y);
        y += rowGap;
    };

    drawRow('Tage', safeStr(back?.absences, ''), safeStr(back?.unexcused_absences, ''));
    drawRow('Lernzeiten', safeStr(back?.learning_times, ''), safeStr(back?.unexcused_learning_times, ''));
    doc.text('Verspätungen:', margin, y);
    doc.text(safeStr(back?.tardiness, ''), margin + labelW, y);
    y += 36;

    // --- Signaturzeile: Datum (26%), dann 30px Abstand, dann 2 Spalten (30px Abstand) ---
    const sigY = doc.internal.pageSize.getHeight() - 165;

    const totalW = pageW - 2 * margin;
    const gap = 30;

    const dateW = totalW * 0.2;

    const remaining = totalW - dateW - gap; // Rest nach Datum + Abstand
    const colGap = 30;
    const colW = (remaining - colGap) / 2;

    const dateX = margin;
    const leftSigX = dateX + dateW + gap;
    const rightSigX = leftSigX + colW + colGap;

    doc.setLineWidth(0.8);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');

    // Datum
    const todayIso = new Date().toISOString().slice(0, 10);
    const dateIso = String(back?.date || '').trim() || todayIso;
    const dateDe = formatDeDate(dateIso) || formatDeDate(todayIso);
    doc.line(dateX, sigY, dateX + dateW, sigY);
    doc.text(dateDe, dateX, sigY - 3);
    doc.text('Datum', dateX, sigY + 12);

    // Schulleiter/in
    doc.line(leftSigX, sigY, leftSigX + colW, sigY);
    doc.text('Schulleiter/in', leftSigX, sigY + 12);

    // Lernbegleiter/in
    doc.line(rightSigX, sigY, rightSigX + colW, sigY);
    doc.text('Lernbegleiter/in', rightSigX, sigY + 12);

    // Eltern
    const parentY = sigY + 80;
    doc.line(margin, parentY, margin + totalW, parentY);
    doc.text('Zur Kenntnis genommen:', margin, parentY - 3);
    doc.text('Unterschrift Eltern', pageW / 2, parentY + 12, {align: 'center'});
}

// --------------------- Fußzeile ---------------------

function drawFooter(doc, {leftText, middleText, rightText}) {
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(120);

    const y = pageH - 24;
    doc.text(safeStr(leftText, ''), 50, y);
    doc.text(safeStr(middleText, ''), pageW / 2, y, {align: 'center'});
    doc.text(safeStr(rightText, ''), pageW - 50, y, {align: 'right'});
}

// --------------------- Haupt-Export ---------------------

export async function exportFeedbackLetterPdf(options) {
    const {
        student,
        frontSheet = null,
        backSheet = null,
        assessmentIds = [],
        assessments: preloadedAssessments = null,
        footnote = 'feedback_letters.footnote',
    } = options || {};

    const studentName =
        typeof student === 'string'
            ? student
            : [safeStr(student?.first_name, ''), safeStr(student?.last_name, '')]
                .filter(Boolean)
                .join(' ')
                .trim();

    // Assessments: bevorzugt bereits geladene + bereits sortierte (vom Backend)
    let assessments = Array.isArray(preloadedAssessments) ? preloadedAssessments : [];

    // Fallback: IDs -> einzeln laden (Legacy-Pfad)
    if (assessments.length === 0) {
        const list = Array.isArray(assessmentIds) ? assessmentIds : [];
        for (const id of list) {
            // eslint-disable-next-line no-await-in-loop
            const a = await getAssessment(id);
            assessments.push(a);
        }

        // Hinweis: In diesem Fallback ist keine Release-Prüfung/Sortierung mehr enthalten.
        // Wer den Legacy-Pfad nutzt, muss dafür sorgen, dass er nur freigegebene + sortierte IDs übergibt.
    }

    const doc = new jsPDF({unit: 'pt', format: 'a4', orientation: 'portrait'});
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    if (typeof doc.setLineHeightFactor === 'function') {
        doc.setLineHeightFactor(1.2);
    }

    // 1) Vorblatt
    await drawFrontSheet(doc, studentName, frontSheet);

    // 2) Raster je Fach (Reihenfolge kommt jetzt aus Backend)
    for (let i = 0; i < assessments.length; i += 1) {
        const a = assessments[i];

        doc.addPage('a4', 'portrait');

        const subj = String(
            a?.subject || a?.assessment_json?.template_snapshot?.subject || ''
        ).trim();

        const isDeutsch = subj.toLowerCase() === 'deutsch';
        const maxPages = isDeutsch ? 2 : 1;

        // eslint-disable-next-line no-await-in-loop
        await drawAssessmentPagesLimited(doc, a, maxPages);
    }

    // 3) Rückblatt
    doc.addPage('a4', 'portrait');
    drawBackSheet(doc, backSheet);

    // Fußzeilen / Seitenzahlen
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p += 1) {
        doc.setPage(p);
        drawFooter(doc, {
            leftText: studentName,
            middleText: footnote,
            rightText: `${p} von ${totalPages}`,
        });
    }

    const sy = safeStr(frontSheet?.school_year, '');
    const name = fileSafe(studentName || 'Lernbrief');
    const tail = sy ? `_${fileSafe(sy)}` : '';
    doc.save(`Lernbrief_${name}${tail}.pdf`);
}
